import type { ReactNode } from 'react';
import { FlexBox } from '@ardoq/layout';
import { TertiaryButton } from '@ardoq/button';
import { ArrowBackIcon, TreeStructureIcon } from '@ardoq/icons';
import { type I18nLocale, t } from '@ardoq/i18n';
import { tourAnchorIds } from '@ardoq/product-tours';
import type { InventoryDatasourceSelection } from '../types';
import { inventoryDatasourceOperations } from '../datasourceOperations';
import { HierarchySwitch } from './HierarchySwitch';

type HierarchyViewControlsProps = {
  datasourceSelection: InventoryDatasourceSelection;
  isHierarchyEnabled: boolean;
  onToggleHierarchy: (
    shouldEnableHierarchy: boolean,
    datasourceSelection: InventoryDatasourceSelection
  ) => Promise<void>;
  onBackToComponentType: VoidFunction;
  i18nLocale: I18nLocale;
};

const HierarchyViewControlsContainer = ({
  children,
}: {
  children: ReactNode;
}) => (
  <FlexBox
    align="center"
    gap="small"
    data-tour-id={tourAnchorIds.inventoryHierarchyControls}
  >
    {children}
  </FlexBox>
);

export const HierarchyViewControls = ({
  datasourceSelection,
  isHierarchyEnabled,
  onToggleHierarchy,
  onBackToComponentType,
  i18nLocale,
}: HierarchyViewControlsProps) => {
  const showFullHierarchyButton =
    inventoryDatasourceOperations.isComponentTypeInventoryDatasourceSelection(
      datasourceSelection
    );

  const showBackToComponentType =
    inventoryDatasourceOperations.isWorkspaceInventoryDatasourceSelection(
      datasourceSelection
    ) && datasourceSelection.hierarchyToggledFromComponentType;

  if (showBackToComponentType) {
    return (
      <HierarchyViewControlsContainer>
        <TertiaryButton onClick={onBackToComponentType}>
          <ArrowBackIcon />
          {t(
            i18nLocale,
            'inventory:interactiveRow.backToComponentType',
            'Back to component type'
          )}
        </TertiaryButton>
      </HierarchyViewControlsContainer>
    );
  }

  if (showFullHierarchyButton) {
    return (
      <HierarchyViewControlsContainer>
        <TertiaryButton
          onClick={() => onToggleHierarchy(true, datasourceSelection)}
        >
          <TreeStructureIcon />
          {t(
            i18nLocale,
            'inventory:interactiveRow.showFullHierarchy',
            'Show full hierarchy'
          )}
        </TertiaryButton>
      </HierarchyViewControlsContainer>
    );
  }

  return (
    <HierarchyViewControlsContainer>
      <HierarchySwitch
        isHierarchyEnabled={isHierarchyEnabled}
        datasourceSelection={datasourceSelection}
        onToggleHierarchy={onToggleHierarchy}
        i18nLocale={i18nLocale}
      />
    </HierarchyViewControlsContainer>
  );
};
